/**
 * Environment Configuration
 * Centralizes all environment variable access with fallbacks
 */

export interface EnvironmentConfig {
    fanClubBaseUrl: string;
    membershipApiUrl: string;
    membershipApiBaseUrl: string;
    stripePublishableKey: string;
    externalSignupUrl: string;
    patreonCampaignUrl: string;
    firebase: {
        apiKey: string;
        authDomain: string;
        projectId: string;
    };
    sessionCookieName: string;
    sessionCookieDomain: string;
    supportEmail: string;
}

/**
 * Get environment-specific configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
    // Determine environment based on hostname or explicit env var
    // Handle test environment where window might not be fully available
    const hostname = typeof window !== 'undefined' && window.location ? window.location.hostname : 'localhost';

    const isDevelopment =
        hostname === 'localhost' ||
        process.env.NODE_ENV === 'development' ||
        process.env.REACT_APP_FIREBASE_USE_EMULATOR === 'true';

    const isQA = hostname.includes('qa');

    const isStaging = hostname.includes('staging');

    // Default to dev URLs for development
    const getDefaultFanClubUrl = () => {
        if (isDevelopment) return 'https://dev.crimejunkie.app';
        if (isQA) return 'https://qa.crimejunkie.app';
        if (isStaging) return 'https://staging.crimejunkie.app';
        return 'https://crimejunkie.app'; // production
    };

    const fanClubBaseUrl = process.env.REACT_APP_MAIN_APP_URL || getDefaultFanClubUrl();

    const sessionCookieDomain = isDevelopment
        ? 'localhost'
        : extractRootDomain(fanClubBaseUrl) || 'crimejunkie.app';

    return {
        fanClubBaseUrl: fanClubBaseUrl,
        membershipApiUrl: process.env.REACT_APP_MEMBERSHIP_API_URL || 'http://localhost:4000/graphql',
        membershipApiBaseUrl: process.env.REACT_APP_MEMBERSHIP_API_BASE_URL || 'http://localhost:4000',
        stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
        externalSignupUrl: process.env.REACT_APP_EXTERNAL_SIGNUP_URL || '',
        patreonCampaignUrl: process.env.REACT_APP_PATREON_CAMPAIGN_URL || 'https://www.patreon.com/join/CrimeJunkie/',
        firebase: {
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
        },
        sessionCookieName: `__session_${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'podfan-dev'}`,
        sessionCookieDomain: sessionCookieDomain,
        supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || 'fanclub@audiochuck.com',
    };
};

/**
 * Environment-specific URLs
 */
export const getAppUrls = () => {

    const config = getEnvironmentConfig();

    return {
        shows: process.env.REACT_APP_FAN_CLUB_SHOWS_URL || config.fanClubBaseUrl + '/app/fan-club/shows',
        signIn: process.env.REACT_APP_FAN_CLUB_SIGN_IN_URL || config.fanClubBaseUrl + '/welcome/sign-in',

        // App Store URLs - update with actual URLs when available
        appStore: process.env.REACT_APP_APPLE_STORE_URL || 'https://apps.apple.com/us/app/crime-junkie/id1530289200',
        googlePlay: process.env.REACT_APP_GOOGLE_PLAY_URL || 'https://play.google.com/store/apps/details?id=com.podfan',

        // Store badge images (already in your public/images/ folder)
        appStoreBadge: '/images/app-store.svg',
        googlePlayBadge: '/images/play-store.png',

        // Terms of Use URL
        termsOfService: process.env.REACT_APP_TERMS_URL || 'https://storage.googleapis.com/podfan-dev.appspot.com/terms.pdf',
    };
};

/**
 * Extracts the root domain from a URL
 * Examples:
 * - 'https://dev.crimejunkie.app' -> 'crimejunkie.app'
 * - 'https://signup.qa.crimejunkie.app' -> 'crimejunkie.app'
 * - 'http://localhost:3000' -> 'localhost'
 * @param url - The URL to parse
 * @returns The root domain or null if parsing fails
 */
const extractRootDomain = (url: string): string | null => {
    try {
        // Handle null/undefined input
        if (!url) {
            return null;
        }

        // Parse the URL
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;

        // Handle localhost
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'localhost';
        }

        // Handle IP addresses
        if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
            return hostname;
        }

        // Split hostname into parts
        const parts = hostname.split('.');

        // For domains with 2 or more parts, return the last 2 parts
        // Examples:
        // - dev.crimejunkie.app -> crimejunkie.app
        // - signup.qa.crimejunkie.app -> crimejunkie.app
        // - crimejunkie.app -> crimejunkie.app
        if (parts.length >= 2) {
            return parts.slice(-2).join('.');
        }

        // Single part hostname (rare case)
        return hostname;
    } catch (error) {
        console.error('Failed to extract root domain from URL:', url, error);
        return null;
    }
};

// Export singleton instance
export const appConfig = getEnvironmentConfig();
export const appUrls = getAppUrls();
